import { Injectable, computed, signal } from '@angular/core';
const CONSENT_KEY = 'handel.cookieConsent';

export interface CookiePreferences {
  necessary: boolean;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
}

const NECESSARY_ONLY: CookiePreferences = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

const ALL_ACCEPTED: CookiePreferences = {
  necessary: true,
  preferences: false,
  analytics: true,
  marketing: true,
};

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly stored = signal<CookiePreferences | null>(this.read());
  private readonly preferencesOpenState = signal(false);

  readonly decided = computed(() => this.stored() !== null);
  readonly preferencesOpen = this.preferencesOpenState.asReadonly();

  constructor() {
    (globalThis as unknown as { resetCookieConsent?: () => void }).resetCookieConsent = () => {
      this.reset();
    };
  }

  preferences(): CookiePreferences {
    return this.stored() ?? { ...NECESSARY_ONLY };
  }

  acceptAll(): void {
    this.persist(ALL_ACCEPTED);
  }

  rejectAll(): void {
    this.persist(NECESSARY_ONLY);
  }

  save(prefs: CookiePreferences): void {
    this.persist(prefs);
  }

  openPreferences(): void {
    this.preferencesOpenState.set(true);
  }

  closePreferences(): void {
    this.preferencesOpenState.set(false);
  }

  reset(): void {
    localStorage.removeItem(CONSENT_KEY);
    this.stored.set(null);
  }

  private persist(prefs: CookiePreferences): void {
    const saved: CookiePreferences = {
      necessary: true,
      preferences: false,
      analytics: prefs.analytics,
      marketing: prefs.marketing,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(saved));
    this.stored.set(saved);
    this.preferencesOpenState.set(false);
  }

  private read(): CookiePreferences | null {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as CookiePreferences;
    } catch {
      return null;
    }
  }
}
